import fs from "fs";
import generateRss from "@/lib/generate-rss";
import { MDXLayoutRenderer } from "@components/blog/MDXComponents";
import {
  formatSlug,
  getAllFilesFrontMatter,
  getFileBySlug,
  getFiles,
} from "@/lib/mdx";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import { AuthorFrontMatter } from "types/AuthorFrontMatter";
import { PostFrontMatter } from "types/PostFrontMatter";
import { Toc } from "types/Toc";
import Layout from "components/platform/Layout";
import FourZeroFour from "pages/404";
import { ReactElement } from "react";

const DEFAULT_LAYOUT = "PostLayout";

export async function getStaticPaths() {
  const posts = getFiles("blog");
  return {
    paths: posts.map((p) => ({
      params: {
        slug: formatSlug(p).split("/"),
      },
    })),
    fallback: false,
  };
}

interface Post {
  mdxSource: string;
  toc: Toc;
  frontMatter: PostFrontMatter;
}

export const getStaticProps: GetStaticProps<{
  post: Post;
  authorDetails: AuthorFrontMatter[];
  prev?: { slug: string; title: string };
  next?: { slug: string; title: string };
}> = async ({ params }: { params?: any }) => {
  // TODO: Check if params can ever be non-optional
  const slug = (params.slug as string[]).join("/");
  const allPosts = await getAllFilesFrontMatter("blog");

  const postIndex = allPosts.findIndex(
    (post) => formatSlug(post.slug) === slug
  );

  const prev: { slug: string; title: string } = allPosts[postIndex + 1] || null;
  const next: { slug: string; title: string } = allPosts[postIndex - 1] || null;
  const post = (await getFileBySlug<PostFrontMatter>("blog", slug)) as Post;

  const authorList = post.frontMatter.authors || ["default"];
  const authorPromise = authorList.map(async (author) => {
    const authorResults = await getFileBySlug<AuthorFrontMatter>("authors", [
      author,
    ]);
    return authorResults.frontMatter;
  });
  const authorDetails = await Promise.all(authorPromise);
  // rss
  if (allPosts.length > 0) {
    const rss = generateRss(allPosts);
    fs.writeFileSync("./public/feed.xml", rss);
  }

  return {
    props: {
      post,
      authorDetails,
      prev,
      next,
    },
  };
};

type Props = InferGetStaticPropsType<typeof getStaticProps>;

const Blog: NextPageTypes.NextPageWithLayout<Props> = ({
  post,
  authorDetails,
  prev,
  next,
}) => {
  const { mdxSource, toc, frontMatter } = post;

  return (
    <>
      {"draft" in frontMatter && frontMatter.draft !== true ? (
        <MDXLayoutRenderer
          layout={frontMatter.layout || DEFAULT_LAYOUT}
          toc={toc}
          mdxSource={mdxSource}
          frontMatter={frontMatter}
          authorDetails={authorDetails}
          prev={prev}
          next={next}
        />
      ) : (
        <FourZeroFour />
      )}
    </>
  );
};

Blog.getLayout = function getLayout(page: ReactElement) {
  return <Layout noTags>{page}</Layout>;
};

export default Blog;
